import { TimeEntryStatus } from '../../../../types';

// Golden-angle hue spacing gives every project a visually distinct color,
// for any number of projects — no fixed-size palette to run out of or wrap around.
const GOLDEN_ANGLE = 137.508;

export const STATUS_CONFIG: Record<TimeEntryStatus, { label: string; bg: string; text: string }> = {
  PENDING_SCRUM:   { label: 'Pending Scrum',   bg: 'bg-yellow-100',  text: 'text-yellow-800' },
  PENDING_LEAD:    { label: 'Pending Lead',     bg: 'bg-orange-100',  text: 'text-orange-800' },
  PENDING_MANAGER: { label: 'Pending Manager',  bg: 'bg-blue-100',    text: 'text-blue-800'   },
  EDIT_REQUESTED:  { label: 'Edit Requested',   bg: 'bg-purple-100',  text: 'text-purple-800' },
  APPROVED:        { label: 'Approved',         bg: 'bg-green-100',   text: 'text-green-800'  },
  REJECTED:        { label: 'Rejected',         bg: 'bg-red-100',     text: 'text-red-800'    },
};

/**
 * The longest status label in the app. Every status pill is made at least this wide, so a
 * column of them lines up. A test keeps it the longest if a label is added.
 */
export const WIDEST_STATUS_LABEL = 'Pending Manager';

export const FROZEN_STATUS_CONFIG = {
  label: 'Project Frozen',
  bg: 'bg-slate-200 dark:bg-slate-700',
  text: 'text-slate-600 dark:text-slate-300',
};

export const CLOSED_STATUS_CONFIG = {
  label: 'Project Closed',
  bg: 'bg-slate-200 dark:bg-slate-700',
  text: 'text-slate-600 dark:text-slate-300',
};


export const HOUR_HEIGHT = 64; // px per hour row

/** The least height an entry block is drawn at, so even a short entry can be clicked. */
export const ENTRY_MIN_HEIGHT = 28;

export function entryBlockHeight(fromMin: number, toMin: number): number {
  return Math.max(((toMin - fromMin) / 60) * HOUR_HEIGHT, ENTRY_MIN_HEIGHT);
}

/**
 * How much of an entry fits in its block. A block is only as tall as the entry
 * is long (a half hour is 32px, an hour 64px), and each step below is the
 * height at which the next line of text fits without touching the one above:
 *
 * - minimal:  the project name
 * - compact:  + hours and status on one line
 * - standard: + the phase (an hour sits here)
 * - full:     hours and status each on their own line, status at the bottom
 */
export type EntryDensity = 'minimal' | 'compact' | 'standard' | 'full';

const COMPACT_FROM = 40;
const STANDARD_FROM = 56;
const FULL_FROM = 72;

export function entryDensity(height: number): EntryDensity {
  if (height >= FULL_FROM) return 'full';
  if (height >= STANDARD_FROM) return 'standard';
  if (height >= COMPACT_FROM) return 'compact';
  return 'minimal';
}
export const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export function getWeekStart(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay(); // 0 = Sunday
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function addDays(date: Date, n: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

// Local Y-M-D, not toISOString() — every other Date here (getWeekStart,
// addDays, the month grid) is built and read in local time. Going through
// toISOString() converts to UTC first, which rolls local midnight back to
// the previous day in any timezone ahead of UTC — shifting every entry into
// the wrong day column.
export function formatDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function toMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + (m || 0);
}

export function getProjectColor(projectId: string, projectIndex: Map<string, number>): string {
  if (!projectIndex.has(projectId)) {
    projectIndex.set(projectId, projectIndex.size);
  }
  const hue = Math.round((projectIndex.get(projectId)! * GOLDEN_ANGLE) % 360);
  return `hsl(${hue} 65% 50%)`;
}

export function withAlpha(hslColor: string, alpha: number): string {
  return hslColor.replace(')', ` / ${alpha})`);
}
